/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useEffect, useRef, useState } from '@wordpress/element';

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import { Spinner } from '@wordpress/components';
import Pagination from './Pagination.component';

import BSHelper from '../BSHelper';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @param {Object}        root0            Component properties.
 * @param {string}        root0.eventType  Event status to request.
 * @param {string}        root0.entityCode Entity code used by the API.
 * @param {number|string} root0.page       Initial page number.
 * @param {number|string} root0.pageSize   Number of events per page.
 * @param {string}        root0.lang       Response language.
 * @param {Object}        root0.phrases    Translated interface phrases.
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Events( {
	eventType,
	entityCode,
	page: initialPage,
	pageSize,
	lang,
	phrases,
} ) {
	const [ libraries, setLibraries ] = useState( [] );
	const [ loading, setLoading ] = useState( false );
	const [ error, setError ] = useState( null );
	const [ loadedPage, setLoadedPage ] = useState( null );
	const [ currentPage, setCurrentPage ] = useState( () => {
		const parsedInitialPage = Number.parseInt( initialPage, 10 );

		return parsedInitialPage > 0 ? parsedInitialPage : 1;
	} );
	const [ totalPages, setTotalPages ] = useState( 1 );
	const eventsListRef = useRef( null );
	const shouldScrollAfterLoadRef = useRef( false );
	const environment = BSHelper.getApiRootUrl();

	useEffect( () => {
		const fetchEvents = async ( requestedPage = 1 ) => {
			setLoading( true ); // Start loading state
			setError( null ); // Clear any previous errors

			try {
				// Make the API request using the provided URL
				const response = await fetch(
					`${ environment }/EventService/api/public/events/${ entityCode }?page=${ requestedPage }&pageSize=${ pageSize }&eventType=${ eventType }`,
					{
						method: 'GET', // GET method for fetching data
						headers: {
							'Accept-Language': lang, // Use the defined language variable
						},
					}
				);
				// console.log('API Response:', response);

				// Check if the response is OK (status code 2xx)
				if ( ! response.ok ) {
					throw new Error( `Error: ${ response.status }` );
				}

				// Parse the response data
				const data = await response.json();

				// Check if the data contains the necessary attributes before setting state
				if ( data && Array.isArray( data.data ) && data.pagination ) {
					const apiTotalPages = Number( data.pagination.totalPages );
					const totalItems = Number(
						data.pagination.countPosts ?? data.pagination.totalPosts
					);
					const itemsPerPage = Number(
						data.pagination.perPage || pageSize
					);
					const calculatedTotalPages =
						itemsPerPage > 0 && totalItems > 0
							? Math.ceil( totalItems / itemsPerPage )
							: 1;
					const resolvedTotalPages =
						apiTotalPages > 0
							? apiTotalPages
							: calculatedTotalPages;

					setTotalPages( resolvedTotalPages );

					if ( currentPage > resolvedTotalPages ) {
						setCurrentPage( resolvedTotalPages );
						return;
					}

					setLibraries( data.data );
					setLoadedPage( requestedPage );
				} else {
					throw new Error( 'Unexpected API response structure' );
				}
			} catch ( requestError ) {
				setError(
					requestError.message || 'An unexpected error occurred'
				); // Set the error state
			} finally {
				setLoading( false ); // Stop loading state
			}
		};

		fetchEvents( currentPage );
	}, [ entityCode, pageSize, eventType, environment, currentPage, lang ] );

	useEffect( () => {
		if (
			! shouldScrollAfterLoadRef.current ||
			loadedPage !== currentPage ||
			! eventsListRef.current
		) {
			return;
		}

		shouldScrollAfterLoadRef.current = false;
		eventsListRef.current.scrollIntoView( {
			behavior: window.matchMedia( '(prefers-reduced-motion: reduce)' )
				.matches
				? 'auto'
				: 'smooth',
			block: 'start',
		} );
	}, [ currentPage, loadedPage ] );

	const handlePageChange = ( nextPage ) => {
		const resolvedPage = Math.min(
			Math.max( Number( nextPage ), 1 ),
			totalPages
		);

		if ( resolvedPage === currentPage ) {
			return;
		}

		shouldScrollAfterLoadRef.current = true;
		setCurrentPage( resolvedPage );
	};

	return (
		<>
			<div
				ref={ eventsListRef }
				className="flex flex-col"
				style={ { scrollMarginTop: '8rem' } }
			>
				{ loading && (
					<div className="flex items-center justify-center w-full">
						<Spinner
							style={ {
								height: 'calc(2px * 20)',
								width: 'calc(2px * 20)',
							} }
						/>
					</div>
				) }

				{ error && <p>{ error }</p> }

				{ ! loading &&
					libraries.map( ( item, index ) => (
						<div
							key={ index }
							className="flex flex-col mb-24 md:justify-between group md:odd:flex-row-reverse md:flex-row"
						>
							<div className="w-full md:w-6/12 pb-6 md:pb-0 overflow-hidden rounded-lg md:group-odd:ms-16 group-even:me-16 h-[360px]">
								<img
									src={
										item.imgUrl !== ''
											? item.imgUrl
											: 'https://placehold.co/130x60?text=Image1+711x360'
									}
									className="object-cover w-full h-full mb-5 lg:mb-0"
									alt=""
									loading="lazy"
								/>
							</div>
							<div className="w-full md:w-6/12 pb-12 md:group-odd:pe-12 md:group-even:ps-12 text-start">
								<h2
									className="text-2xl font-bold text-body"
									dangerouslySetInnerHTML={ {
										__html: BSHelper.truncate(
											item.title,
											60
										),
									} }
								></h2>
								<p
									className="py-4 pb-8 lg:py-10"
									dangerouslySetInnerHTML={ {
										__html: BSHelper.truncate(
											item.description,
											140
										),
									} }
								></p>
								{ item.btnDirection !== '' && (
									<a
										target="_blank"
										href={ BSHelper.addLangParam(
											item.btnDirection,
											lang
										) }
										className="w-48 px-12 py-3 my-5 text-white transition-all border border-secondary bg-secondary lg:mt-0 hover:bg-white hover:text-secondary hover:border h-14 rounded-xl"
										rel="noreferrer"
									>
										{ phrases.learn_more }
									</a>
								) }
							</div>
						</div>
					) ) }
			</div>
			{ ! loading && totalPages > 1 && (
				<Pagination
					currentPage={ currentPage }
					totalPages={ totalPages }
					onPageChange={ handlePageChange }
				/>
			) }
		</>
	);
}
